import { ILogService, IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { Observable, take } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit, urlFromMap } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { HomeScreenViewValuesEnum } from "@gtmhub/home/components/home-screen/home-screen.component";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { IMetric, MetricService } from "@gtmhub/okrs/metrics";
import { IChecklist, OnboardingService } from "@gtmhub/onboarding";
import {
  IPlanningSession,
  ISessionsStoreState,
  PlanningSessionService,
  PlanningSessionsActions,
  filterActiveSessions,
  filterOpenedActiveAndFutureSessions,
} from "@gtmhub/sessions";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ITask, TaskService } from "@gtmhub/tasks";
import { ITeam } from "@gtmhub/teams";
import { toIdMap } from "@gtmhub/util";
import { REFLECTION_CADENCE_DURATION_IN_DAYS } from "@webapp/reflections/components/team-reflections/team-reflections.component";
import { IReflection, IReflectionUpdate, ITodoReflection } from "@webapp/reflections/models/reflections.models";
import { ReflectionsFacade } from "@webapp/reflections/services/reflections-facade.service";
import { getLastDayOfCadence, getLastMondayAsDate } from "@webapp/reflections/utils";
import { AssigneeActions } from "../../assignees/redux/assignee-actions";
import { IEmployee } from "../../employees/models/models";
import { GoalService } from "../../goals/goal.service";
import { IGoal } from "../../goals/models";
import { IHubSessionIdWorkflowMap, IHubWorkflowItemsCollection } from "../../hub/models/hub";
import { ICollection } from "../../models";
import { TeamService } from "../../teams/team.service";
import { getCurrentUserId } from "../../users";
import { CurrentUserService } from "../../users/currentUserService";
import { HomeService, IHomeMetric } from "../services/home";
import { isToDoMetric } from "../utils";

const bindStateToScope = (state: ISessionsStoreState & IAssigneesStoreState & ICurrentEmployeeStoreState) => {
  const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const { items: sessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { info: currentEmployee, isFetched: currentEmployeeFetched, error: currentEmployeeError } = state.currentEmployee;
  // home screen relies only on currently active sessions which have started and not ended yet
  // anything else out is considered as a non active session and should not persist on home screen
  const activeSessions = filterActiveSessions(sessions);

  return {
    reduxStatesInitialized: assigneesFetched && sessionsFetched && currentEmployeeFetched,
    reduxStateError: assigneesError || sessionsError || currentEmployeeError,
    accountUsersCount: Object.values(assignees).reduce((totalCount: number, assignee) => (assignee.type === "user" ? (totalCount += 1) : totalCount), 0),
    allSessions: sessions,
    allSessionsIds: sessions.map((s) => s.id),
    activeOpenSessions: filterOpenedActiveAndFutureSessions(sessions),
    activeSessions,
    activeSessionIds: activeSessions.map((session) => session.id),
    activeSessionsMap: toIdMap(activeSessions),
    employee: currentEmployee,
  };
};

type IHomeItem = "todo" | "myOkrs";

export type IHomeTeam = Pick<ITeam, "id" | "accountId" | "name" | "picture" | "color" | "avatar" | "isActive">;

export interface IHomeCollapsedItems {
  todo: boolean;
  myOkrs: boolean;
  myTeamsOkrs: Record<string, boolean>;
}

export interface IHomeTodoItems {
  tasks: ITask[];
  metrics: IHomeMetric[];
  goalsToApprove: IGoal[];
}

export interface IHomeCtrlScope extends IHomeCtrlRedux, IModalScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  isReflectionsFeatureEnabled: boolean;
  isReflectionsFeatureModuleEnabled: boolean;
  employee: IEmployee;
  skeletonTemplateUrl: string;
  todoItems: IHomeTodoItems;
  todoReflections: ITodoReflection[];
  indicators: {
    loading?: boolean;
  };
  reduxStatesInitialized: boolean;
  reduxStateError: IUIError;
  homeItems: {
    todo: boolean;
    myOkrs: boolean;
  };
  myOkrs: IGoal[];
  teams: IHomeTeam[];
  activeSessionIds: string[];
  activeSessionsMap: { [key: string]: IPlanningSession };
  activeSessions: IPlanningSession[];
  activeOpenSessions: IPlanningSession[];
  allSessions: IPlanningSession[];
  allSessionsIds: string[];
  shouldSeeOnboardingExperiment: boolean;
  shouldSeeFirstOkrGuide: boolean;
  shouldSeeHomeFeedButtons: boolean;
  activeScreen: HomeScreenViewValuesEnum;
  toggleSection(item: IHomeItem): void;
  updateHomeCollapsedItems(item: Partial<IHomeCollapsedItems>): void;
  homeCollapsedItems: IHomeCollapsedItems;
  isOwnerAccessFeatureEnabled: boolean;
}

export interface IHomeCtrlRedux {
  accountUsersCount: number;
}

export class HomeCtrl implements IStateInit {
  private strings = {
    taskStatuses: ["inprogress", "todo"],
  };

  private allReviewableGoals: IHubWorkflowItemsCollection = null;
  private tasks: ICollection<ITask> = null;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "CurrentUserService",
    "UIErrorHandlingService",
    "HomeService",
    "GoalService",
    "PlanningSessionService",
    "TaskService",
    "TeamService",
    "$ngRedux",
    "AssigneeActions",
    "MetricService",
    "PlanningSessionsActions",
    "CurrentEmployeeActions",
    "isBootstrap",
    "OnboardingService",
    "$log",
    "$timeout",
    "FeatureToggleService",
    "ReflectionsFacade",
  ];

  constructor(
    private $scope: IHomeCtrlScope,
    private $rootScope: ITraceRootScopeService,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private homeService: HomeService,
    private goalService: GoalService,
    private planningSessionService: PlanningSessionService,
    private taskService: TaskService,
    private teamService: TeamService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private metricService: MetricService,
    private planningSessionsActions: PlanningSessionsActions,
    private currentEmployeeActions: CurrentEmployeeActions,
    private isBootstrap: boolean,
    private onboardingService: OnboardingService,
    private $log: ILogService,
    private $timeout: ITimeoutService,
    private featureToggleService: FeatureToggleService,
    private reflectionsFacade: ReflectionsFacade
  ) {
    // if account is bootstrapping or browser window is refreshed -> show app skeleton; else show home skeleton keeping left nav hidden
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const skeletonTemplateUrl = this.isBootstrap ? require("../../shared/views/app-skeleton.jade") : require("../views/home-skeleton.jade");
    this.$scope.skeletonTemplateUrl = urlFromMap(skeletonTemplateUrl);

    this.$scope.indicators = {
      loading: true,
    };

    this.$scope.todoItems = {
      tasks: [],
      metrics: [],
      goalsToApprove: [],
    };
    this.$scope.todoReflections = [];

    this.$scope.homeItems = {
      todo: true,
      myOkrs: true,
    };

    this.$scope.toggleSection = this.toggleSection;
    this.$scope.updateHomeCollapsedItems = this.updateHomeCollapsedItems;

    const okrEvents = new OkrEvents($scope);
    okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
    this.$scope.$on("toDoItemsEmptied", this.onToDoItemsEmptied);
    const removeCloseFirstOkrGuideListener = this.$rootScope.$on("closeFirstOkrGuide", () => this.checkIfShouldSeeFirstOkrGuide());
    const removeFirstOkrCreatedListener = this.$rootScope.$on("firstOkrCreated", () => this.checkIfShouldSeeFirstOkrGuide());
    const removeHomeScreenChangedListener = this.$rootScope.$on("homeScreenChanged", () => this.getActiveHomeScreen());
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.$scope.$on("$destroy", () => {
      unsubscribe();
      removeCloseFirstOkrGuideListener();
      removeFirstOkrCreatedListener();
      removeHomeScreenChangedListener();
    });
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxStatesInitialized,
        () => this.$scope.reduxStateError
      )
      .then(
        () => this.initializeData(),
        () => this.handleError(this.$scope.reduxStateError)
      )
      .catch((error) => this.handleError(error));
  }

  private checkReflectionsFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReflections).then((enabled) => {
      this.$scope.isReflectionsFeatureEnabled = enabled;
    });
  };

  private checkReflectionsFeatureModule = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleReflections).then((enabled) => {
      this.$scope.isReflectionsFeatureModuleEnabled = enabled;
    });
  };

  private checkOwnerAccessFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.OwnerAccessToOkrs).then((enabled) => {
      this.$scope.isOwnerAccessFeatureEnabled = enabled;
    });
  };

  private getHomeCollapsedItems = (): void => {
    const setting = this.currentUserService.getUserSetting<IHomeCollapsedItems>("homeCollapsedItems");
    const myTeamsOkrs = this.$scope.teams.reduce<Record<string, boolean>>((teamsMap, team: ITeam) => {
      teamsMap[team.id] = typeof setting?.myTeamsOkrs[team.id] === "boolean" ? setting.myTeamsOkrs[team.id] : true;
      return teamsMap;
    }, {});

    this.$scope.homeCollapsedItems = setting ? { ...setting, myTeamsOkrs } : { todo: true, myOkrs: true, myTeamsOkrs };
  };

  private updateHomeCollapsedItems = (item: Partial<IHomeCollapsedItems>): void => {
    this.$rootScope.traceAction("toggle_section", () => {
      const newSetting = { ...this.$scope.homeCollapsedItems, ...item };
      this.$scope.homeCollapsedItems = newSetting;

      this.currentUserService.setUserSetting({ homeCollapsedItems: newSetting });
    });
  };

  private toggleSection = (item: IHomeItem): void => {
    this.$scope.homeItems[item] = !this.$scope.homeItems[item];
  };

  private initializeData = () => {
    const featureflagPromises: IPromise<void>[] = [this.checkOwnerAccessFeatureToggle(), this.checkReflectionsFeatureToggle(), this.checkReflectionsFeatureModule()];
    return this.$q.all(featureflagPromises).then(() => {
      const initializationPromises: IPromise<unknown>[] = [this.getTasks(), this.getReviewableGoals(), this.getTeams()];
      this.getActiveHomeScreen();

      // get metrics depends on tasks, reviewable goals to build todo-items section
      return this.$q.all(initializationPromises).then(() => {
        this.getHomeCollapsedItems();

        return this.$q
          .all({
            goals: this.getOkrs(),
            reflections: this.getTodoReflections(),
          })
          .then(({ goals, reflections }) => {
            this.$scope.todoReflections = reflections;
            this.$scope.shouldSeeFirstOkrGuide = goals.length ? this.onboardingService.shouldSeeFirstOkrGuide() : false;
            this.$rootScope.$broadcast("goalsToApproveLoaded", this.$scope.todoItems.goalsToApprove);

            this.$scope.indicators.loading = false;
            this.$scope.shouldSeeOnboardingExperiment = this.onboardingService.shouldSeeOnboardingExperiment();

            const checklist = this.currentUserService.getUserSetting("checklist") as IChecklist;

            this.$scope.shouldSeeHomeFeedButtons =
              !this.$scope.shouldSeeOnboardingExperiment || (this.$scope.shouldSeeOnboardingExperiment && checklist && checklist.stepsV1 && checklist.stepsV1.createdOkr);
          });
      });
    });
  };

  private areReflectionsEnabled = (): boolean => {
    return this.$scope.isReflectionsFeatureEnabled && this.$scope.isReflectionsFeatureModuleEnabled;
  };

  private checkIfShouldSeeFirstOkrGuide = () => {
    this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide();
  };

  private getActiveHomeScreen = () => {
    this.$scope.activeScreen = this.currentUserService.getUserSetting("homeScreen") || HomeScreenViewValuesEnum.dashboard;
  };

  private getOkrs = (): IPromise<IGoal[]> => {
    const employeeIdAndTeamIds = this.homeService.getEmployeeTeamIds(this.$scope.employee);
    const mongoMetricsFilter: IRestLayerRequest = {
      filter: {
        $and: [{ ownerIds: { $in: employeeIdAndTeamIds } }, { sessionId: { $in: this.$scope.activeSessionIds } }],
      },
      // *****lastCheckinDate must go with latestSnapshotAt otherwise the endpoint doesn't return the field*****
      fields:
        "id,goalId,name,sessionId,ownerIds,color,attainment,dueDate,manualType,lastCheckInDate,latestSnapshotAt,cascadeType,sourceMetricId,goalClosedStatus,orderId",
      limit: -1,
    };

    if (this.$scope.isOwnerAccessFeatureEnabled) {
      mongoMetricsFilter.filter = {
        $and: [
          { ownerIds: { $in: employeeIdAndTeamIds } },
          { $or: [{ sessionId: { $in: this.$scope.activeSessionIds } }, { sessionId: { $nin: this.$scope.allSessionsIds } }] },
        ],
      };
    }
    return this.metricService.getMetricsV2(mongoMetricsFilter).then((metrics) => {
      this.setHomeTodoItems(metrics.items);

      return this.getGoals(metrics.items, employeeIdAndTeamIds).then((goals) => {
        this.$scope.myOkrs = this.concatGoalsOkrs(goals.items, metrics.items);
        return goals.items;
      });
    });
  };

  private getTodoReflections = (): IPromise<ITodoReflection[]> => {
    const reflectionsDefer = this.$q.defer<ITodoReflection[]>();

    // TODO reflections -> remove condition when feature is released
    if (this.areReflectionsEnabled()) {
      this.getCurrentUserReflections()
        .pipe(take(1))
        .subscribe({
          next: (reflectionData) => {
            this.getCurrentUserReflectionUpdatesForCurrentCadence(reflectionData.items.map((r) => r.id))
              .pipe(take(1))
              .subscribe({
                next: (updatesData) => {
                  const lastMondayAsDate = getLastMondayAsDate();
                  const currentCadenceEndDate = getLastDayOfCadence(lastMondayAsDate, REFLECTION_CADENCE_DURATION_IN_DAYS);

                  const todoReflections = this.homeService.getReflectionsNotUpdatedInCurrentCadence(reflectionData.items, updatesData.items).map((r) => ({
                    id: r.id,
                    teamId: r.teamId,
                    dueDate: currentCadenceEndDate,
                    questions: r.questions,
                  }));

                  reflectionsDefer.resolve(todoReflections);
                },
                error: () => reflectionsDefer.resolve([]),
              });
          },
          error: () => reflectionsDefer.resolve([]),
        });
    } else {
      reflectionsDefer.resolve([]);
    }

    return reflectionsDefer.promise;
  };

  private getCurrentUserReflections = (): Observable<ICollection<IReflection>> => {
    const userBelongsToTeamIds = this.homeService.getEmployeeTeamIds(this.$scope.employee);
    const reflectionsFilter = { filter: { $and: [{ teamId: { $in: userBelongsToTeamIds } }, { isEnabled: { $ne: false } }] } };

    return this.reflectionsFacade.getReflections$(reflectionsFilter);
  };

  private getCurrentUserReflectionUpdatesForCurrentCadence = (reflectionIds: string[]): Observable<ICollection<IReflectionUpdate>> => {
    const lastMondayAsDate = getLastMondayAsDate();
    const currentCadenceStartDate = lastMondayAsDate;
    const currentCadenceEndDate = getLastDayOfCadence(lastMondayAsDate, REFLECTION_CADENCE_DURATION_IN_DAYS);

    const updatesFilter = {
      filter: {
        $and: [
          { createdAt: { $gte: currentCadenceStartDate } },
          { createdAt: { $lte: currentCadenceEndDate } },
          { reflectionId: { $in: reflectionIds } },
          { userId: getCurrentUserId() },
        ],
      },
    };

    return this.reflectionsFacade.getReflectionUpdates$(updatesFilter);
  };

  private setHomeTodoItems = (metrics: IMetric[]): void => {
    this.$scope.todoItems = this.getEmployeeTodoItems(metrics.filter(isToDoMetric), this.tasks.items, this.allReviewableGoals.hubWorkflowItems);
  };

  private getReviewableGoals = (): IPromise<IHubWorkflowItemsCollection | void> => {
    const approvalWorkflowItems = "hubWorkflowItems{goalsToReview{id,name,currentUserApproved},itemsToReview}";

    return this.planningSessionService.getCurrentUserWorkflowItems(approvalWorkflowItems, "-approved,draft").then((allReviewableGoals) => {
      this.allReviewableGoals = allReviewableGoals;
    });
  };

  private getTasks = (): IPromise<ICollection<ITask> | void> => {
    const mongoTaskFilter: IRestLayerRequest = {
      filter: {
        $and: [
          { ownerId: this.$scope.employee.id },
          { status: { $in: this.strings.taskStatuses } }, // todo and in progress tasks only
          {
            $or: [{ sessionId: { $exists: false } }, { sessionId: { $in: this.$scope.activeSessionIds } }], // don't belong to a session, or belong to an active one
          },
          { $or: [{ archived: { $exists: false } }, { archived: false }] },
        ],
      },
      fields: "dueDate,name,parentId",
    };

    if (this.$scope.isOwnerAccessFeatureEnabled) {
      mongoTaskFilter.filter = {
        $and: [
          { ownerId: this.$scope.employee.id },
          { status: { $in: this.strings.taskStatuses } }, // todo and in progress tasks only
          {
            $or: [{ sessionId: { $exists: false } }, { sessionId: { $in: this.$scope.activeSessionIds } }, { sessionId: { $nin: this.$scope.allSessionsIds } }], // don't belong to a session, or belong to an active one
          },
          { $or: [{ archived: { $exists: false } }, { archived: false }] },
        ],
      };
    }
    return this.taskService.getTasksV2(mongoTaskFilter).then((tasks) => {
      this.tasks = tasks;
    });
  };

  private getTeams = (): IPromise<ITeam[] | void> => {
    const teamIds = this.homeService.getEmployeeTeamIds(this.$scope.employee);
    const mongoTeamFilter = {
      filter: {
        _id: { $in: teamIds },
      },
      fields: "name,picture,color,avatar,isActive",
    };

    return (
      this.teamService
        .getTeams(mongoTeamFilter)
        .then((teams: ICollection<IHomeTeam>) => {
          this.$scope.teams = teams.items;
        })
        // catch is needed here as getTeams method is not returned where invoked
        .catch((error) => this.handleError(error))
    );
  };

  private getGoals = (metrics: IMetric[], employeeIdAndTeamIds: string[]): IPromise<ICollection<IGoal>> => {
    const isIdUnique = (goalId, index, self) => self.indexOf(goalId) === index;
    const goalIds = metrics
      .map((metric) => {
        if (!metric.goalId) {
          this.$log.warn("A metric should not exist if it has no goal id. Metric ID:" + metric.id);
        }
        return metric.goalId;
      })
      .filter((goalId, index, self) => goalId && isIdUnique(goalId, index, self));

    // getting goal by metric.goalId which we know the metrics are for current active sessions
    // or we are getting them by onwerId where we pass activeSessionIds
    const mongoGoalFilter = {
      filter: {
        $or: [{ _id: { $in: goalIds } }, { $and: [{ ownerIds: { $in: employeeIdAndTeamIds } }, { sessionId: { $in: this.$scope.activeSessionIds } }] }],
      },
      fields: "attainment,name,ownerIds,dateFrom,dateTo,sessionId,closedStatus,parentId,parentType,private",
      sort: "closedStatus.status,duration,name",
    };

    return this.goalService.getGoalsV2(mongoGoalFilter);
  };

  private concatGoalsOkrs = (goals: IGoal[], metrics: IMetric[]): IGoal[] => {
    const allGoalIds: string[] = [];
    const allGoals = goals.map((goal) => {
      allGoalIds.push(goal.id);
      return { ...goal, metrics: metrics.filter((metric) => metric.goalId === goal.id) || [] };
    });
    const shadowGoal: IGoal = {
      ownerIds: [],
      metrics: [],
    };
    shadowGoal.metrics = metrics.filter((metric) => !allGoalIds.length || !allGoalIds.includes(metric.goalId));

    return this.$scope.isOwnerAccessFeatureEnabled && shadowGoal.metrics.length ? [...allGoals, shadowGoal] : allGoals;
  };

  private getEmployeeTodoItems = (metrics: IMetric[], tasks: ITask[], allReviewableGoals: IHubSessionIdWorkflowMap): IHomeTodoItems => {
    return {
      metrics: this.homeService.getNotUpdatedMetricsInCurrentCadence(metrics as IHomeMetric[], this.$scope.activeSessionsMap, this.$scope.employee), // filtering metrics not updated in current cadance
      tasks, // tasks are already filtered by deadline from API
      goalsToApprove: this.homeService.getGoalsToApprove(allReviewableGoals, this.$scope.allSessions), // filtering goals to review whcih user hasn't approved yer
    };
  };

  private onToDoItemsEmptied = (): void => {
    this.$scope.todoItems = {
      tasks: [],
      metrics: [],
      goalsToApprove: [],
    };
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    this.$scope.shouldSeeHomeFeedButtons = true;

    if (args.reason === "goalStatusChanged") {
      const oldTodoMetrics = this.$scope.todoItems.metrics.slice();
      const oldMyOkrs = this.$scope.myOkrs.slice();

      // we cannot rely on binding change because bound variables are not used in todo and my-okrs views
      // other collections are iterated in child directives
      // $watches in child scopes may be triggered by other changes so custom events are broadcasted to notify change
      this.getOkrs().then(() => {
        // timeout is needed because of array value reassigning - this.$scope.myOkrs
        this.$timeout(() => {
          this.$scope.$broadcast("homeTodoItemsUpdated", { oldTodoMetrics });
          this.$scope.$broadcast("homeMyOkrsUpdated", { oldMyOkrs });
        });
      });
    }
  };

  private handleError = <E>(error: E): void => {
    this.uiErrorHandlingService.handleModal(error);

    // if account is bootstrapping or browser window is refreshed and an error is caught -> swap skeletons so left nav becomes visible
    if (this.isBootstrap) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const skeletonTemplateUrl = require("../views/home-skeleton.jade");
      this.$scope.skeletonTemplateUrl = urlFromMap(skeletonTemplateUrl);
    }
  };
}
